// src/pages/Payment/PaymentStatusPage.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import { toast } from "react-toastify";
import { track } from "../../utils/analytics";

const TERMINAL_STATUSES = new Set(["Paid", "Failed", "Expired"]);
const STATUS_ENDPOINT = checkoutSessionId =>
  `/payment/checkout/${checkoutSessionId}/status`;
const DETAILS_ENDPOINT = checkoutSessionId =>
  `/payment/checkout/${checkoutSessionId}`;
const FAKEPAY_ENDPOINT = checkoutSessionId =>
  `/payment/checkout/${checkoutSessionId}/pay-fake`;
const SUBSCRIPTION_ENDPOINT = "/payment/subscription";
const WALLET_SUCCESS_RETURN_PATH = "/app/welcomepage?topupSuccess=1";

const formatDate = utc => {
  if (!utc) return "-";
  const d = new Date(utc);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const unwrapResponse = res => {
  const body = res?.data ?? {};
  const ok = typeof body.ok === "boolean" ? body.ok : true;
  const data = body?.data ?? body;
  const message = body?.message || data?.message || "";
  return { ok, data, message };
};

const formatAmount = (amount, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(amount)) ? Number(amount) : 0);

const normalizeMoneyFields = data => {
  const toNumber = value => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  return {
    baseAmount: toNumber(data?.baseAmount ?? data?.subtotalAmount ?? data?.amount),
    cycleDiscountAmount: toNumber(data?.cycleDiscountAmount),
    discountAmount: toNumber(data?.discountAmount),
    taxAmount: toNumber(data?.gstAmount ?? data?.taxAmount),
    finalAmount: toNumber(data?.finalAmount),
    currency: data?.currency || "INR",
  };
};

const isExpiredSignal = ({ statusCode, code, message, dataStatus }) => {
  if (statusCode === 404 || statusCode === 410) return true;
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (c.includes("EXPIRED")) return true;
  const m = String(message || "").toLowerCase();
  if (m.includes("expired") || m.includes("timeout")) return true;
  const s = String(dataStatus || "").toLowerCase();
  return s === "expired" || s === "timeout";
};

const normalizeStatus = (rawStatus, { alreadyPaid, expiresAtUtc }) => {
  if (alreadyPaid) return "Paid";

  const now = Date.now();
  if (expiresAtUtc) {
    const expiresAtMs = new Date(expiresAtUtc).getTime();
    if (!Number.isNaN(expiresAtMs) && expiresAtMs < now) return "Expired";
  }

  const value = String(rawStatus || "")
    .trim()
    .toLowerCase();

  if (!value) return "Pending";
  if (value === "paid" || value === "success" || value === "succeeded")
    return "Paid";
  if (
    value === "failed" ||
    value === "failure" ||
    value === "cancelled" ||
    value === "canceled"
  ) {
    return "Failed";
  }
  if (value === "expired" || value === "timeout" || value === "timedout")
    return "Expired";

  return "Pending";
};

const formatRemainingTime = remainingMs => {
  if (remainingMs == null || remainingMs <= 0) return "00:00";

  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};

function PaymentStatusPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search || "");
  const source = searchParams.get("source") || "checkout";
  const isWalletSource = source === "wallet";
  const orderId = searchParams.get("orderId") || null;
  const checkoutSessionIdParam = searchParams.get("checkoutSessionId");
  const fallbackSessionId = searchParams.get("sessionId");
  const clientRequestId = searchParams.get("crid") || null;
  // Canonical id: checkoutSessionId (fallback to legacy sessionId for compatibility).
  const checkoutSessionId = checkoutSessionIdParam || fallbackSessionId || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [statusData, setStatusData] = useState(null);
  const [status, setStatus] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [fakePayLoading, setFakePayLoading] = useState(false);
  const [expiresAtUtc, setExpiresAtUtc] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [sessionDetailsLoading, setSessionDetailsLoading] = useState(false);
  const [sessionDetailsError, setSessionDetailsError] = useState("");
  const [sessionDetails, setSessionDetails] = useState(null);
  const previousStatusRef = useRef(null);
  const terminalEventsRef = useRef({
    paid: false,
    failed: false,
    expired: false,
  });
  const hasRedirectedOnSuccessRef = useRef(false);

  const preferredDetailsExpiresAtUtc = sessionDetails?.expiresAtUtc || null;
  const statusAnalyticsBaseProps = useMemo(
    () => ({
      clientRequestId,
      source,
    }),
    [clientRequestId, source]
  );

  const fetchSessionDetails = useCallback(async () => {
    if (!checkoutSessionId) return;

    try {
      setSessionDetailsLoading(true);
      setSessionDetailsError("");

      const res = await axiosClient.get(DETAILS_ENDPOINT(checkoutSessionId), {
        headers: { "x-suppress-403-toast": true },
      });
      const { ok, data, message } = unwrapResponse(res);
      const statusCode = res?.status;
      const code = data?.code || data?.errorCode || "";
      const dataStatus = data?.status || data?.paymentStatus || data?.state || "";
      const expiresFromDetails =
        data?.expiresAtUtc || data?.expiresAt || data?.expiresAtUtcString || null;

      if (
        !ok &&
        isExpiredSignal({
          statusCode,
          code,
          message,
          dataStatus,
        })
      ) {
        setStatus("Expired");
        setError("Checkout session expired");
        setExpiresAtUtc(expiresFromDetails);
        setSessionDetails(null);
        return;
      }

      if (!ok) {
        const msg = message || "Unable to load order details.";
        setSessionDetailsError(msg);
        setSessionDetails(null);
        return;
      }

      const money = normalizeMoneyFields(data);
      const normalized = {
        ...money,
        couponCode: data?.couponCode || null,
        couponId: data?.couponId || null,
        planId: data?.planId || null,
        planName: data?.planName || null,
        billingCycle: data?.billingCycle || null,
        currentPeriodEndUtc: data?.currentPeriodEndUtc || null,
        createdAtUtc: data?.createdAtUtc || data?.createdAt || null,
        expiresAtUtc: expiresFromDetails,
        invoiceId: data?.invoiceId || null,
        paymentTransactionId: data?.paymentTransactionId || null,
      };

      setSessionDetails(normalized);
      if (normalized.expiresAtUtc) {
        // Prefer snapshot expiry for countdown when available.
        setExpiresAtUtc(normalized.expiresAtUtc);
      }

      if (
        isExpiredSignal({
          statusCode,
          code,
          message,
          dataStatus,
        })
      ) {
        setStatus("Expired");
        setError("Checkout session expired");
      }
    } catch (err) {
      const statusCode = err?.response?.status;
      const message =
        err?.response?.data?.message || "Unable to load order details.";
      const code =
        err?.response?.data?.code || err?.response?.data?.errorCode || "";

      if (
        isExpiredSignal({
          statusCode,
          code,
          message,
          dataStatus: "",
        })
      ) {
        setStatus("Expired");
        setError("Checkout session expired");
        setSessionDetails(null);
        return;
      }

      setSessionDetailsError(message);
      setSessionDetails(null);
    } finally {
      setSessionDetailsLoading(false);
    }
  }, [checkoutSessionId]);

  const fetchStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (!checkoutSessionId) {
        const msg = "Missing checkout session id. Please start checkout again.";
        setError(msg);
        setStatus("Failed");
        setStatusData(null);
        setAlreadyPaid(false);
        setExpiresAtUtc(null);
        if (!silent) setLoading(false);
        return { terminal: true, status: "Failed", alreadyPaid: false };
      }

      if (!silent) setLoading(true);

      try {
        const res = await axiosClient.get(
          STATUS_ENDPOINT(checkoutSessionId),
          {
            headers: { "x-suppress-403-toast": true },
          }
        );
        const { ok, data, message } = unwrapResponse(res);

        if (!ok) {
          const msg = message || "Unable to fetch checkout status.";
          const isExpiredMessage = /expired|timeout/i.test(msg);
          const nextStatus = isExpiredMessage ? "Expired" : "Pending";
          const nextExpiresAtUtc =
            preferredDetailsExpiresAtUtc ||
            data?.expiresAtUtc ||
            data?.expiresAt ||
            data?.expiresAtUtcString ||
            null;

          setError(msg);
          setAlreadyPaid(false);
          setStatus(nextStatus);
          setExpiresAtUtc(nextExpiresAtUtc);
          setStatusData({
            checkoutSessionId,
            status: nextStatus,
            rawStatus: null,
            invoiceId: null,
            paymentTransactionId: null,
            expiresAtUtc: nextExpiresAtUtc,
          });

          return {
            terminal: TERMINAL_STATUSES.has(nextStatus),
            status: nextStatus,
            alreadyPaid: false,
          };
        }

        const nextAlreadyPaid = Boolean(data?.alreadyPaid);
        const rawStatus = data?.status || data?.paymentStatus || data?.state;
        const nextExpiresAtUtc =
          preferredDetailsExpiresAtUtc ||
          data?.expiresAtUtc ||
          data?.expiresAt ||
          data?.expiresAtUtcString ||
          null;
        const nextStatus = normalizeStatus(rawStatus, {
          alreadyPaid: nextAlreadyPaid,
          expiresAtUtc: nextExpiresAtUtc,
        });

        setError("");
        setAlreadyPaid(nextAlreadyPaid);
        setStatus(nextStatus);
        setExpiresAtUtc(nextExpiresAtUtc);
        setStatusData({
          checkoutSessionId: data?.checkoutSessionId || checkoutSessionId,
          status: nextStatus,
          rawStatus: rawStatus || null,
          invoiceId: data?.invoiceId || null,
          paymentTransactionId: data?.paymentTransactionId || null,
          expiresAtUtc: nextExpiresAtUtc,
        });

        return {
          terminal: TERMINAL_STATUSES.has(nextStatus) || nextAlreadyPaid,
          status: nextStatus,
          alreadyPaid: nextAlreadyPaid,
        };
      } catch (err) {
        const msg =
          err?.response?.data?.message || "Unable to fetch checkout status.";
        const statusCode = err?.response?.status;
        const isExpiredMessage = /expired|timeout/i.test(msg);
        const nextStatus = isExpiredMessage
          ? "Expired"
          : statusCode === 404 || statusCode === 400
          ? "Failed"
          : "Pending";

        setError(msg);
        setAlreadyPaid(false);
        setStatus(nextStatus);
        setStatusData(prev => ({
          checkoutSessionId,
          status: nextStatus,
          rawStatus: prev?.rawStatus || null,
          invoiceId: prev?.invoiceId || null,
          paymentTransactionId: prev?.paymentTransactionId || null,
          expiresAtUtc: prev?.expiresAtUtc || null,
        }));

        return {
          terminal: isExpiredMessage || statusCode === 404 || statusCode === 400,
          status: nextStatus,
          alreadyPaid: false,
        };
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [checkoutSessionId, preferredDetailsExpiresAtUtc]
  );

  useEffect(() => {
    if (!checkoutSessionId) return;
    fetchSessionDetails();
  }, [checkoutSessionId, fetchSessionDetails]);

  useEffect(() => {
    previousStatusRef.current = null;
    terminalEventsRef.current = { paid: false, failed: false, expired: false };
  }, [checkoutSessionId]);

  useEffect(() => {
    if (!checkoutSessionId) return;
    void track("checkout_status_viewed", {
      checkoutSessionId,
      source,
    }, statusAnalyticsBaseProps);
  }, [checkoutSessionId, source, statusAnalyticsBaseProps]);

  const expiresAtMs = useMemo(() => {
    const resolvedExpiresAtUtc = preferredDetailsExpiresAtUtc || expiresAtUtc;
    if (!resolvedExpiresAtUtc) return null;
    const parsed = new Date(resolvedExpiresAtUtc).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }, [expiresAtUtc, preferredDetailsExpiresAtUtc]);

  const remainingMs = useMemo(() => {
    if (expiresAtMs == null) return null;
    return expiresAtMs - nowMs;
  }, [expiresAtMs, nowMs]);

  const remainingLabel = useMemo(
    () => formatRemainingTime(remainingMs),
    [remainingMs]
  );

  const isLocallyExpired =
    remainingMs != null &&
    remainingMs <= 0 &&
    !alreadyPaid &&
    status !== "Paid";

  const shouldStopPolling =
    !checkoutSessionId ||
    alreadyPaid ||
    TERMINAL_STATUSES.has(status) ||
    isLocallyExpired;

  useEffect(() => {
    if (expiresAtMs == null || shouldStopPolling) return undefined;

    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAtMs, shouldStopPolling]);

  useEffect(() => {
    if (!isLocallyExpired) return;
    setStatus("Expired");
    setError("Checkout session expired");
    setStatusData(prev => ({
      checkoutSessionId: prev?.checkoutSessionId || checkoutSessionId,
      status: "Expired",
      rawStatus: prev?.rawStatus || null,
      invoiceId: prev?.invoiceId || null,
      paymentTransactionId: prev?.paymentTransactionId || null,
      expiresAtUtc: prev?.expiresAtUtc || expiresAtUtc || null,
    }));
  }, [checkoutSessionId, expiresAtUtc, isLocallyExpired]);

  useEffect(() => {
    let isCancelled = false;
    let timer = null;

    if (shouldStopPolling) {
      setLoading(false);
      return () => {};
    }

    setAttempts(0);

    const startPolling = async () => {
      const first = await fetchStatus();
      if (isCancelled || first?.terminal) return;

      timer = setInterval(async () => {
        setAttempts(prev => prev + 1);
        const result = await fetchStatus({ silent: true });
        const expiredNow =
          expiresAtMs != null &&
          Date.now() >= expiresAtMs &&
          !alreadyPaid &&
          status !== "Paid";
        if (!isCancelled && (result?.terminal || expiredNow) && timer) {
          clearInterval(timer);
          timer = null;
        }
      }, 2000);
    };

    startPolling();

    return () => {
      isCancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [alreadyPaid, expiresAtMs, fetchStatus, shouldStopPolling, status]);

  const handleFakePay = async () => {
    if (!checkoutSessionId || isLocallyExpired || TERMINAL_STATUSES.has(status)) {
      return;
    }

    if (process.env.NODE_ENV !== "production") {
      void track("checkout_fakepay_clicked", { checkoutSessionId }, statusAnalyticsBaseProps);
    }

    try {
      setFakePayLoading(true);
      const res = await axiosClient.post(FAKEPAY_ENDPOINT(checkoutSessionId), null, {
        headers: { "x-suppress-403-toast": true },
      });
      const { ok, data, message } = unwrapResponse(res);

      if (!ok) {
        toast.error(message || "FakePay request failed.");
        return;
      }

      if (data?.alreadyPaid) {
        toast.info("Checkout session is already paid.");
      } else {
        toast.success("FakePay payment completed.");
      }

      // Optimistically mark as paid in UI; backend polling will reconcile details.
      setAlreadyPaid(true);
      setStatus("Paid");
      setError("");
      await redirectToUpgradeSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to run FakePay.";
      toast.error(msg);
    } finally {
      setFakePayLoading(false);
    }
  };

  const goToBilling = () =>
    navigate(isWalletSource ? WALLET_SUCCESS_RETURN_PATH : "/app/settings/billing");
  const goToDashboard = () =>
    navigate(isWalletSource ? WALLET_SUCCESS_RETURN_PATH : "/app/dashboard");
  const goBack = () => navigate(-1);

  const viewState = useMemo(() => {
    if (!checkoutSessionId) return "failed";
    if (loading && !status && !error) return "checking";
    if (alreadyPaid || status === "Paid") return "success";
    if (status === "Failed") return "failed";
    if (status === "Expired") return "expired";
    if (status === "Pending") return "pending";
    return "checking";
  }, [alreadyPaid, checkoutSessionId, error, loading, status]);

  const title =
    viewState === "success"
      ? "Payment successful"
      : viewState === "expired"
      ? "Session expired"
      : viewState === "failed"
      ? "Payment failed"
      : viewState === "pending"
      ? "Payment pending / processing"
      : "Confirming your payment...";

  const message =
    error ||
    (viewState === "success"
      ? isWalletSource
        ? "Your payment is confirmed and wallet balance refresh is in progress."
        : "Your payment is confirmed and subscription activation is in progress."
      : viewState === "expired"
      ? "Session expired. Please start a new checkout."
      : viewState === "failed"
      ? "We could not confirm payment for this checkout session."
      : viewState === "pending"
      ? "Your payment is being processed. This page will refresh automatically."
      : "We are syncing your payment with billing. This usually takes a moment.");

  const badgeColor =
    viewState === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : viewState === "failed" || viewState === "expired"
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-slate-50 text-slate-700 border-slate-200";

  const canUseFakePay =
    process.env.NODE_ENV !== "production" &&
    checkoutSessionId &&
    !TERMINAL_STATUSES.has(status) &&
    !isLocallyExpired;

  const redirectToUpgradeSuccess = useCallback(async () => {
    if (hasRedirectedOnSuccessRef.current) return;
    hasRedirectedOnSuccessRef.current = true;

    if (isWalletSource) {
      navigate(WALLET_SUCCESS_RETURN_PATH, { replace: true });
      return;
    }

    // Prefer freshly paid checkout snapshot over subscription endpoint (which may lag briefly).
    let planName = sessionDetails?.planName || statusData?.planName || "";
    let billingCycle =
      sessionDetails?.billingCycle || statusData?.billingCycle || "";
    let periodEndUtc = sessionDetails?.currentPeriodEndUtc || "";

    try {
      const res = await axiosClient.get(SUBSCRIPTION_ENDPOINT, {
        headers: { "x-suppress-403-toast": true },
      });
      const { ok, data } = unwrapResponse(res);
      if (ok && data) {
        if (!planName) {
          planName = data?.planName || "";
        }
        if (!billingCycle) {
          billingCycle = data?.billingCycle || "";
        }
        if (!periodEndUtc) {
          periodEndUtc = data?.currentPeriodEndUtc || "";
        }
      }
    } catch {
      // Redirect with available snapshot details even if subscription fetch fails.
    }

    const query = new URLSearchParams();
    query.set("upgradeSuccess", "1");
    query.set("refreshHint", "1");
    if (planName) query.set("planName", planName);
    if (billingCycle) query.set("billingCycle", String(billingCycle).toLowerCase());
    if (periodEndUtc) query.set("periodEndUtc", periodEndUtc);

    navigate(`/app/welcomepage?${query.toString()}`, { replace: true });
  }, [
    isWalletSource,
    navigate,
    sessionDetails?.billingCycle,
    sessionDetails?.currentPeriodEndUtc,
    sessionDetails?.planName,
    statusData?.billingCycle,
    statusData?.planName,
  ]);

  useEffect(() => {
    if (viewState !== "success") return undefined;
    const timer = setTimeout(() => {
      void redirectToUpgradeSuccess();
    }, 1500);
    return () => clearTimeout(timer);
  }, [redirectToUpgradeSuccess, viewState]);

  const eventInvoiceId = statusData?.invoiceId || sessionDetails?.invoiceId || null;
  const eventPaymentTransactionId =
    statusData?.paymentTransactionId || sessionDetails?.paymentTransactionId || null;

  useEffect(() => {
    if (!checkoutSessionId || !status) return;

    const fromStatus = previousStatusRef.current;
    const toStatus = status;
    if (fromStatus === toStatus) return;

    void track("checkout_status_transition", {
      checkoutSessionId,
      fromStatus: fromStatus || null,
      toStatus,
      alreadyPaid,
      invoiceId: eventInvoiceId,
      paymentTransactionId: eventPaymentTransactionId,
    }, statusAnalyticsBaseProps);

    if (toStatus === "Paid" && !terminalEventsRef.current.paid) {
      terminalEventsRef.current.paid = true;
      void track("checkout_status_paid", {
        checkoutSessionId,
        alreadyPaid,
        invoiceId: eventInvoiceId,
        paymentTransactionId: eventPaymentTransactionId,
      }, statusAnalyticsBaseProps);
    }

    if (toStatus === "Failed" && !terminalEventsRef.current.failed) {
      terminalEventsRef.current.failed = true;
      void track("checkout_status_failed", {
        checkoutSessionId,
        alreadyPaid,
        invoiceId: eventInvoiceId,
        paymentTransactionId: eventPaymentTransactionId,
      }, statusAnalyticsBaseProps);
    }

    if (toStatus === "Expired" && !terminalEventsRef.current.expired) {
      terminalEventsRef.current.expired = true;
      void track("checkout_status_expired", {
        checkoutSessionId,
        alreadyPaid,
        invoiceId: eventInvoiceId,
        paymentTransactionId: eventPaymentTransactionId,
      }, statusAnalyticsBaseProps);
    }

    previousStatusRef.current = toStatus;
  }, [
    alreadyPaid,
    checkoutSessionId,
    eventInvoiceId,
    eventPaymentTransactionId,
    statusAnalyticsBaseProps,
    status,
  ]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-10 flex items-start justify-center">
      <div className="w-full max-w-xl">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900">
                {title}
              </h1>
              <p className="text-[10px] sm:text-xs text-slate-600 mt-1">
                {message}
              </p>
              {(viewState === "pending" || viewState === "checking") &&
                expiresAtMs != null && (
                  <p className="text-[10px] sm:text-xs text-amber-700 mt-1">
                    Session expires in: {remainingLabel}
                  </p>
                )}
              {viewState === "expired" && (
                <p className="text-[10px] sm:text-xs text-red-600 mt-1">
                  Session expired. Please start a new checkout.
                </p>
              )}
            </div>
            <div
              className={
                "px-2.5 py-1 rounded-full border text-[8px] font-medium " +
                badgeColor
              }
            >
              {viewState === "checking" && "Checking status"}
              {viewState === "pending" && "Pending confirmation"}
              {viewState === "success" && "Payment confirmed"}
              {viewState === "failed" && "Payment not confirmed"}
              {viewState === "expired" && "Session expired"}
            </div>
          </div>

          {(source || orderId || checkoutSessionId) && (
            <div className="text-[8px] text-slate-500 space-y-0.5">
              {source && (
                <div>
                  Source: <span className="font-semibold">{source}</span>
                </div>
              )}
              {orderId && (
                <div>
                  Order: <span className="font-mono font-semibold">{orderId}</span>
                </div>
              )}
              {checkoutSessionId && (
                <div>
                  Checkout Session:{" "}
                  <span className="font-mono font-semibold">
                    {checkoutSessionId}
                  </span>
                </div>
              )}
            </div>
          )}

          {(sessionDetailsLoading || sessionDetails || sessionDetailsError) && (
            <div className="mt-1 p-3 rounded-lg bg-slate-50 border border-slate-100 text-[9px] text-slate-600 space-y-1">
              <div className="text-[9px] text-slate-500 mb-1">Order Summary</div>
              {sessionDetailsLoading && !sessionDetails ? (
                <div>Loading order details...</div>
              ) : sessionDetailsError && !sessionDetails ? (
                <div>Unable to load order details</div>
              ) : (
                <>
                  {(sessionDetails?.planName || sessionDetails?.planId) && (
                    <div>
                      {isWalletSource ? "Flow" : "Plan"}:{" "}
                      <span className="font-medium text-slate-900">
                        {sessionDetails?.planName || sessionDetails?.planId}
                      </span>
                    </div>
                  )}
                  {!isWalletSource && sessionDetails?.billingCycle && (
                    <div>
                      Billing cycle:{" "}
                      <span className="font-medium text-slate-900">
                        {sessionDetails.billingCycle}
                      </span>
                    </div>
                  )}
                  <div>
                    {isWalletSource ? "Amount" : "Base amount"}:{" "}
                    <span className="font-medium text-slate-900">
                      {formatAmount(
                        sessionDetails?.baseAmount,
                        sessionDetails?.currency
                      )}
                    </span>
                  </div>
                  {Number(sessionDetails?.cycleDiscountAmount || 0) > 0 && (
                    <div>
                      Cycle discount:{" "}
                      <span className="font-medium text-emerald-700">
                        -{" "}
                        {formatAmount(
                          sessionDetails?.cycleDiscountAmount,
                          sessionDetails?.currency
                        )}
                      </span>
                    </div>
                  )}
                  {Number(sessionDetails?.discountAmount || 0) > 0 && (
                    <div>
                      Discount:{" "}
                      <span className="font-medium text-emerald-700">
                        -{" "}
                        {formatAmount(
                          sessionDetails?.discountAmount,
                          sessionDetails?.currency
                        )}
                      </span>
                    </div>
                  )}
                  <div>
                    Tax/GST:{" "}
                    <span className="font-medium text-slate-900">
                      {formatAmount(
                        sessionDetails?.taxAmount,
                        sessionDetails?.currency
                      )}
                    </span>
                  </div>
                  <div>
                    Final amount:{" "}
                    <span className="font-semibold text-slate-900">
                      {formatAmount(
                        sessionDetails?.finalAmount,
                        sessionDetails?.currency
                      )}
                    </span>
                  </div>
                  {(sessionDetails?.couponCode || sessionDetails?.couponId) && (
                    <div>
                      Coupon:{" "}
                      <span className="font-medium text-slate-900">
                        {sessionDetails?.couponCode || sessionDetails?.couponId}
                      </span>
                    </div>
                  )}
                  {sessionDetails?.createdAtUtc && (
                    <div>
                      Created:{" "}
                      <span className="font-medium text-slate-900">
                        {formatDate(sessionDetails.createdAtUtc)}
                      </span>
                    </div>
                  )}
                  {sessionDetails?.expiresAtUtc && (
                    <div>
                      Expires:{" "}
                      <span className="font-medium text-slate-900">
                        {formatDate(sessionDetails.expiresAtUtc)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {(statusData?.invoiceId ||
            sessionDetails?.invoiceId ||
            statusData?.paymentTransactionId ||
            sessionDetails?.paymentTransactionId ||
            statusData?.expiresAtUtc ||
            sessionDetails?.expiresAtUtc) && (
            <div className="mt-1 p-3 rounded-lg bg-slate-50 border border-slate-100 text-[9px] text-slate-600 space-y-1">
              {(statusData?.invoiceId || sessionDetails?.invoiceId) && (
                <div>
                  Invoice ID:{" "}
                  <span className="font-mono text-slate-900">
                    {statusData?.invoiceId || sessionDetails?.invoiceId}
                  </span>
                </div>
              )}
              {(statusData?.paymentTransactionId ||
                sessionDetails?.paymentTransactionId) && (
                <div>
                  Payment Transaction ID:{" "}
                  <span className="font-mono text-slate-900">
                    {statusData?.paymentTransactionId ||
                      sessionDetails?.paymentTransactionId}
                  </span>
                </div>
              )}
              {(statusData?.expiresAtUtc || sessionDetails?.expiresAtUtc) && (
                <div>
                  Expires:{" "}
                  <span className="font-medium text-slate-900">
                    {formatDate(
                      sessionDetails?.expiresAtUtc || statusData?.expiresAtUtc
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {viewState === "success" && (
              <>
                <button
                  onClick={goToBilling}
                  className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[10px] font-semibold hover:bg-black"
                >
                  {isWalletSource ? "Go to Overview" : "Go to Billing"}
                </button>
                <button
                  onClick={goToDashboard}
                  className="px-3 py-1.5 rounded-md border border-slate-300 text-[10px] text-slate-800 hover:bg-slate-50"
                >
                  {isWalletSource ? "View Overview" : "View Subscription"}
                </button>
              </>
            )}

            {(viewState === "pending" || viewState === "checking") && (
              <>
                <button
                  onClick={() => fetchStatus()}
                  className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[10px] font-semibold hover:bg-black"
                >
                  Refresh Status
                </button>
                <button
                  onClick={goToBilling}
                  className="px-3 py-1.5 rounded-md border border-slate-300 text-[10px] text-slate-800 hover:bg-slate-50"
                >
                  {isWalletSource ? "Open Overview" : "Open Billing Page"}
                </button>
              </>
            )}

            {(viewState === "failed" || viewState === "expired") && (
              <>
                <button
                  onClick={goToBilling}
                  className="px-3 py-1.5 rounded-md bg-red-600 text-white text-[10px] font-semibold hover:bg-red-700"
                >
                  {viewState === "expired"
                    ? isWalletSource ? "Start new top-up" : "Start new checkout"
                    : isWalletSource ? "Back to Overview" : "Back to Billing"}
                </button>
                <button
                  onClick={goBack}
                  className="px-3 py-1.5 rounded-md border border-slate-300 text-[10px] text-slate-800 hover:bg-slate-50"
                >
                  Go Back
                </button>
              </>
            )}

            {canUseFakePay && (
              <button
                onClick={handleFakePay}
                disabled={fakePayLoading || isLocallyExpired || TERMINAL_STATUSES.has(status)}
                className="px-3 py-1.5 rounded-md border border-amber-300 text-[10px] text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-70"
              >
                {fakePayLoading ? "Running FakePay..." : "FakePay (dev)"}
              </button>
            )}
          </div>

          {attempts > 0 && (
            <div className="text-[7px] text-slate-400 mt-2">
              Checked status {attempts + 1} time(s).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentStatusPage;
